/**
 * MSW Overrides: Outstanding Balance scenario
 *
 * Reproduces an OVERDUE subscription with two unpaid invoices so the
 * OutstandingBalanceModal can be tested end-to-end in local development.
 *
 * Enable by setting ENABLE_MSW=true and MSW_OVERRIDE=true in your .env,
 * then restart the dev server.
 */

import type { HttpHandler } from 'msw';
import { http, HttpResponse } from 'msw';
import { BFF_ENDPOINTS, createMockOverdueInvoices, toMSWPattern } from '@cvo/domain';
import type { SubscriptionResponse } from '@cvo/domain';

const mockOverdueSubscription: SubscriptionResponse = {
  id: '4fb77ff9-ec6e-41fd-a407-2e9bdaf930bf',
  status: 'OVERDUE',
  displayStatus: 'OVERDUE',
  commitment: {
    amount: 6,
    unit: 'MONTHS',
    endDate: '2026-08-31T23:59:59Z',
  },
  gracePeriod: {
    amount: 14,
    unit: 'DAYS',
    endDate: '2026-04-02T23:59:59Z',
  },
  renewalTerms: {
    billingCycleDay: 1,
    frequency: 'MONTHLY',
    billingAlignment: 'TENANT_BCD',
  },
  plan: {
    code: 'VEL-BASIC-SOLO-RT1-MONTHLY-PLAN',
    addons: [
      {
        code: 'VEL-UNDERSEATBAG-INCLUDED-MONTHLY-PLAN',
        product: {
          code: 'VEL-UNDERSEATBAG-INCLUDED-MONTHLY',
          ancillary: 'UNDERSEATBAG',
        },
      },
    ],
    product: {
      code: 'BASIC-SOLO-RT1-MONTHLY',
      family: 'BASIC',
      tripType: 'RT',
      group: 'SOLO',
    },
    startDate: '2026-03-20T12:16:00Z',
  },
  cancellationEligibility: ['IMMEDIATE', 'END_OF_COMMITMENT'],
  price: {
    amount: '59.00',
    currency: 'EUR',
  },
  effectivePrice: { gross: { amount: '59.00', currency: 'EUR' }, net: { amount: '59.00', currency: 'EUR' } },
  effectivePriceSchedule: [],
  quota: {
    limit: 1,
    used: 0,
    usage: 'MONTHLY',
    type: 'PER_JOURNEY',
    unlimited: false,
    periodStart: '2026-03-01T00:00:00Z',
    periodEnd: '2026-03-31T23:59:59Z',
  },
};

export function createOutstandingBalanceOverrides(baseUrl: string): HttpHandler[] {
  const sanitizedBaseUrl = baseUrl.replace(/\/$/, '');

  return [
    // Override the subscription endpoint to return OVERDUE display status
    http.get(
      `${sanitizedBaseUrl}/${toMSWPattern(BFF_ENDPOINTS.SUBSCRIPTION)}`,
      () => HttpResponse.json(mockOverdueSubscription),
    ),

    // Override the invoices endpoint to return two unpaid invoices
    http.get(
      `${sanitizedBaseUrl}/${toMSWPattern(BFF_ENDPOINTS.INVOICES)}`,
      () => {
        return HttpResponse.json(createMockOverdueInvoices());
      },
    ),
  ];
}
